// 7-segment + LED-bulb digit primitives, ported from the scoreboard
// design ref. Both render arbitrary digits + a colon glyph. The
// mode-aware Digit / ClockDisplay components below pick the right
// renderer based on the tenant's `digitStyle` setting.

import type { DigitStyle } from './themes';

// ─── 7-segment ──────────────────────────────────────────────────────

const SEG_MAP: Record<string, number[]> = {
  '0': [1, 1, 1, 1, 1, 1, 0],
  '1': [0, 1, 1, 0, 0, 0, 0],
  '2': [1, 1, 0, 1, 1, 0, 1],
  '3': [1, 1, 1, 1, 0, 0, 1],
  '4': [0, 1, 1, 0, 0, 1, 1],
  '5': [1, 0, 1, 1, 0, 1, 1],
  '6': [1, 0, 1, 1, 1, 1, 1],
  '7': [1, 1, 1, 0, 0, 0, 0],
  '8': [1, 1, 1, 1, 1, 1, 1],
  '9': [1, 1, 1, 1, 0, 1, 1],
  ' ': [0, 0, 0, 0, 0, 0, 0],
};

export function Seg7({
  ch,
  color = '#ff1a1a',
  h = 48,
}: {
  ch: string;
  color?: string;
  h?: number;
}) {
  const segs = SEG_MAP[ch] || SEG_MAP[' '];
  const w = h * 0.58;
  const t = Math.max(h * 0.1, 2);
  const g = Math.max(h * 0.025, 0.5);
  const dim = 'rgba(40,20,20,0.5)';
  const glow = `0 0 ${h * 0.12}px ${color}88, 0 0 ${h * 0.3}px ${color}22`;
  const P: React.CSSProperties[] = [
    { left: t + g, right: t + g, top: g, height: t },
    { right: g, width: t, top: t + g * 2, height: h / 2 - t * 1.5 - g },
    { right: g, width: t, top: h / 2 + t * 0.5 + g, height: h / 2 - t * 1.5 - g },
    { left: t + g, right: t + g, bottom: g, height: t },
    { left: g, width: t, top: h / 2 + t * 0.5 + g, height: h / 2 - t * 1.5 - g },
    { left: g, width: t, top: t + g * 2, height: h / 2 - t * 1.5 - g },
    { left: t + g, right: t + g, top: h / 2 - t / 2, height: t },
  ];
  return (
    <div style={{ width: w, height: h, position: 'relative', flexShrink: 0 }}>
      {P.map((s, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            ...s,
            borderRadius: t * 0.25,
            background: segs[i] ? color : dim,
            boxShadow: segs[i] ? glow : 'none',
          }}
        />
      ))}
    </div>
  );
}

function Seg7Num({
  val,
  digits = 2,
  color = '#ff1a1a',
  h = 48,
}: {
  val: number | string;
  digits?: number;
  color?: string;
  h?: number;
}) {
  const str = String(val).padStart(digits, ' ');
  return (
    <div style={{ display: 'flex', gap: h * 0.07 }}>
      {str.split('').map((c, i) => (
        <Seg7 key={i} ch={c} color={color} h={h} />
      ))}
    </div>
  );
}

function Seg7Colon({ color = '#ff1a1a', h = 48 }: { color?: string; h?: number }) {
  const r = Math.max(h * 0.06, 2);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: h * 0.2,
        width: h * 0.18,
        height: h,
      }}
    >
      <div style={dot(r, color)} />
      <div style={dot(r, color)} />
    </div>
  );
}

// ─── LED bulb ────────────────────────────────────────────────────────

const BULB_FONT: Record<string, string[]> = {
  '0': ['01110', '10001', '10001', '10001', '10001', '10001', '01110'],
  '1': ['00100', '01100', '10100', '00100', '00100', '00100', '11111'],
  '2': ['01110', '10001', '00001', '00110', '01000', '10000', '11111'],
  '3': ['01110', '10001', '00001', '00110', '00001', '10001', '01110'],
  '4': ['00110', '01010', '10010', '10010', '11111', '00010', '00010'],
  '5': ['11111', '10000', '11110', '00001', '00001', '10001', '01110'],
  '6': ['01110', '10000', '10000', '11110', '10001', '10001', '01110'],
  '7': ['11111', '00001', '00010', '00100', '01000', '01000', '01000'],
  '8': ['01110', '10001', '10001', '01110', '10001', '10001', '01110'],
  '9': ['01110', '10001', '10001', '01111', '00001', '00001', '01110'],
  ' ': ['00000', '00000', '00000', '00000', '00000', '00000', '00000'],
};

export function BulbDigit({
  ch,
  color = '#ff1a1a',
  sz = 4,
  gp = 1.5,
}: {
  ch: string;
  color?: string;
  sz?: number;
  gp?: number;
}) {
  const pat = BULB_FONT[ch] || BULB_FONT[' '];
  const dim = `${color}08`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: gp }}>
      {pat.map((row, y) => (
        <div key={y} style={{ display: 'flex', gap: gp }}>
          {row.split('').map((b, x) => (
            <div
              key={x}
              style={{
                width: sz,
                height: sz,
                borderRadius: '50%',
                background: b === '1' ? color : dim,
                boxShadow: b === '1' ? `0 0 ${sz}px ${color}77` : 'none',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function BulbNum({
  val,
  digits = 2,
  color = '#ff1a1a',
  sz = 4,
  gp = 1.5,
}: {
  val: number | string;
  digits?: number;
  color?: string;
  sz?: number;
  gp?: number;
}) {
  const str = String(val).padStart(digits, ' ');
  return (
    <div style={{ display: 'flex', gap: gp * 3 }}>
      {str.split('').map((c, i) => (
        <BulbDigit key={i} ch={c} color={color} sz={sz} gp={gp} />
      ))}
    </div>
  );
}

function BulbColon({ color = '#ff1a1a', sz = 4 }: { color?: string; sz?: number }) {
  const h = 7 * sz + 6 * 1.5;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: sz * 3,
        width: sz * 2,
        height: h,
      }}
    >
      <div style={dot(sz / 2, color)} />
      <div style={dot(sz / 2, color)} />
    </div>
  );
}

// ─── Broadcast clean ────────────────────────────────────────────────
// Crisp TV-style numerals: heavy tabular digits with a soft glow. No
// segments or bulbs — reads cleanly at any size for streaming overlays.

function BroadcastText({
  text,
  color,
  h,
}: {
  text: string;
  color: string;
  h: number;
}) {
  return (
    <div
      style={{
        fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
        fontWeight: 800,
        fontSize: h * 0.92,
        lineHeight: 1,
        letterSpacing: '-0.04em',
        fontVariantNumeric: 'tabular-nums',
        color,
        textShadow: `0 0 ${h * 0.08}px ${color}66, 0 2px ${h * 0.03}px rgba(0,0,0,0.6)`,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </div>
  );
}

// ─── Mode-aware wrappers ────────────────────────────────────────────

export function Digit({
  val,
  digits,
  color,
  h,
  mode,
}: {
  val: number | string;
  digits: number;
  color: string;
  h: number;
  mode: DigitStyle;
}) {
  if (mode === 'bulb') {
    const sz = h > 50 ? 6 : h > 30 ? 3.5 : 2.2;
    const gp = h > 50 ? 2 : h > 30 ? 1.2 : 0.8;
    return <BulbNum val={val} digits={digits} color={color} sz={sz} gp={gp} />;
  }
  if (mode === 'broadcast') {
    return (
      <BroadcastText text={String(val).padStart(digits, '0')} color={color} h={h} />
    );
  }
  return <Seg7Num val={val} digits={digits} color={color} h={h} />;
}

export function ClockDisplay({
  m,
  s,
  color,
  h,
  mode,
}: {
  m: number;
  s: number;
  color: string;
  h: number;
  mode: DigitStyle;
}) {
  if (mode === 'bulb') {
    const sz = h > 50 ? 5 : 4;
    const gp = h > 50 ? 1.8 : 1.3;
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <BulbNum val={m} digits={1} color={color} sz={sz} gp={gp} />
        <BulbColon color={color} sz={sz} />
        <BulbNum val={String(s).padStart(2, '0')} digits={2} color={color} sz={sz} gp={gp} />
      </div>
    );
  }
  if (mode === 'broadcast') {
    return (
      <BroadcastText text={`${m}:${String(s).padStart(2, '0')}`} color={color} h={h} />
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Seg7Num val={m} digits={1} color={color} h={h} />
      <Seg7Colon color={color} h={h} />
      <Seg7Num val={String(s).padStart(2, '0')} digits={2} color={color} h={h} />
    </div>
  );
}

// ─── Black "digit well" — recessed background for any digit cluster ──

export function Well({
  children,
  pad = '6px 8px',
  width,
}: {
  children: React.ReactNode;
  pad?: string;
  width?: number | string;
}) {
  return (
    <div
      style={{
        background: '#080808',
        borderRadius: 3,
        padding: pad,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.9)',
        border: '1px solid #1a1a1a',
        width: width ?? 'auto',
      }}
    >
      {children}
    </div>
  );
}

function dot(r: number, color: string): React.CSSProperties {
  return {
    width: r * 2,
    height: r * 2,
    borderRadius: '50%',
    background: color,
    boxShadow: `0 0 ${r * 3}px ${color}88`,
  };
}
